/**
 * VerifyFlow — top-level Verify page content.
 *
 * Three input paths:
 *  - Drop a `.gi` file → parse client-side, then call `verifyBundle`
 *  - Paste a root_id → call `verifyById`
 *  - Verify File tab → drop any file, check embedded proof via API
 *
 * Result view animates in; a "Verify another" affordance clears state.
 */

import { useMutation } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { Card, CardSubtitle, CardTitle } from '@/components/Card';
import { ErrorState } from '@/components/ErrorState';
import { Spinner } from '@/components/LoadingState';
import { verify } from '@/api/endpoints/verify';
import type { GiBundle, VerificationResult } from '@/api/types';
import { cn } from '@/utils/cn';

import { DropZone } from './DropZone';
import { VerificationResultView } from './VerificationResult';
import { VerifyFileTab } from './VerifyFileTab';

type ActiveTab = 'gi' | 'file';

export function VerifyFlow() {
  const { t } = useTranslation('verify');
  const [activeTab, setActiveTab] = useState<ActiveTab>('gi');
  const [pasteId, setPasteId] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);

  const verifyById = useMutation({
    mutationFn: async (id: string): Promise<VerificationResult> => {
      const r = await verify.byId(id.trim());
      if (!r) throw new Error(t('errors.not_found'));
      return r;
    },
  });

  const verifyBundle = useMutation({
    mutationFn: (b: GiBundle) => verify.byBundle(b),
  });

  const pending = verifyById.isPending || verifyBundle.isPending;
  const result = verifyById.data ?? verifyBundle.data;
  const error = verifyById.isError
    ? (verifyById.error as Error).message
    : verifyBundle.isError
      ? (verifyBundle.error as Error).message
      : parseError;

  const reset = () => {
    verifyById.reset();
    verifyBundle.reset();
    setPasteId('');
    setParseError(null);
  };

  if (result) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col gap-6">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-ink">{t('title')}</h1>
            <p className="mt-1 text-sm text-ink-muted">{t('subtitle')}</p>
          </div>
          <Button variant="secondary" size="sm" onClick={reset}>
            {t('result.verify_another')}
          </Button>
        </header>
        <VerificationResultView result={result} />
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">{t('title')}</h1>
        <p className="mt-1 text-sm text-ink-muted">{t('subtitle')}</p>
      </header>

      {/* Tab bar */}
      <div className="flex gap-1 rounded-lg border border-border bg-surface p-1" role="tablist">
        <TabButton
          active={activeTab === 'gi'}
          onClick={() => setActiveTab('gi')}
          id="tab-gi"
          controls="panel-gi"
        >
          Verify GI
        </TabButton>
        <TabButton
          active={activeTab === 'file'}
          onClick={() => setActiveTab('file')}
          id="tab-file"
          controls="panel-file"
        >
          Verify File
        </TabButton>
      </div>

      {/* GI verification panel */}
      <div
        id="panel-gi"
        role="tabpanel"
        aria-labelledby="tab-gi"
        hidden={activeTab !== 'gi'}
        className={cn('flex flex-col gap-6', activeTab !== 'gi' ? 'hidden' : '')}
      >
        {error ? (
          <ErrorState
            title={t('errors.title')}
            body={error}
            onRetry={() => {
              verifyById.reset();
              verifyBundle.reset();
              setParseError(null);
            }}
          />
        ) : null}

        <DropZone
          onBundle={(b) => {
            setParseError(null);
            verifyBundle.mutate(b);
          }}
          onError={(m) => setParseError(m)}
          disabled={pending}
        />

        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-border" />
          <span className="text-[11px] uppercase tracking-wider text-ink-subtle">
            {t('or')}
          </span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <Card>
          <CardTitle>{t('paste.title')}</CardTitle>
          <CardSubtitle>{t('paste.sub')}</CardSubtitle>
          <form
            className="mt-3 flex flex-col gap-3 sm:flex-row"
            onSubmit={(e) => {
              e.preventDefault();
              if (pasteId.trim()) verifyById.mutate(pasteId);
            }}
          >
            <input
              type="text"
              value={pasteId}
              onChange={(e) => setPasteId(e.target.value)}
              placeholder="00000000-0000-4000-8000-000000000000"
              className={cn(
                'flex-1 rounded-md border border-border bg-surface px-3 py-2 font-mono text-xs text-ink',
                'placeholder:text-ink-subtle focus-visible:border-spark',
              )}
            />
            <Button type="submit" variant="primary" size="md" disabled={!pasteId.trim() || pending}>
              {pending ? <Spinner /> : t('paste.submit')}
            </Button>
          </form>
        </Card>

        {pending && !result ? (
          <p className="text-center text-sm text-ink-muted">{t('pending')}</p>
        ) : null}
      </div>

      {/* File verification panel */}
      <div
        id="panel-file"
        role="tabpanel"
        aria-labelledby="tab-file"
        hidden={activeTab !== 'file'}
        className={cn(activeTab !== 'file' ? 'hidden' : '')}
      >
        <VerifyFileTab />
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  id,
  controls,
  children,
}: {
  active: boolean;
  onClick: () => void;
  id: string;
  controls: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      id={id}
      aria-controls={controls}
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
        active
          ? 'bg-canvas text-ink shadow-subtle'
          : 'text-ink-muted hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}
